import * as React from "react";
import { useMemo } from "react";
import _ from "lodash";
import ImageGroup from "components/ImageGroup";
import LoadingContainer from "components/LoadingContainer";
import Text from "components/LegacyText";
import Thumbnail from "components/Thumbnail";
import Money from "components/Money";
import cx from "classnames";
import variables from "styles/variables";
import styles from "./styles.scss";
import { Mobile } from "components/Layout/Responsive";
import { GigReportOverviewFragment, GigReportStepsQuery, TemperatureUnit } from "gql-gen";
import Button from "components/Button";
import { Atom, atomToLabel } from "modules/Report/StepForm";
import { zipFile } from "utilities/filestack";
import { toCelsius, toFahrenheit } from "utilities/temperature";
import { downloadUrl } from "utilities/files";
import moment from "moment-timezone";
import { format, parseISO } from "date-fns";
import { parseGigReportSteps } from "../parseGigReportSteps";
import StatsBoard from "components/StatsBoard";
import StatsItem from "components/StatsBoard/StatsItem";

type Group = GigReportOverviewFragment["report"][0];
type Subgroup = Group["subgroups"][0];
type Question = Subgroup["questions"][0];

interface Props {
  gig: GigReportOverviewFragment | null;
  gigReportSteps: GigReportStepsQuery["gigReportSteps"] | null;
  loadingGigDetails: boolean;
}

// Helper to check if question is unanswered (null or "unasked")
const isUnanswered = (answerState: string | null | undefined) => {
  return answerState === null || answerState === "unasked" || answerState === undefined;
};

function GroupView({ questionGroup, isMobile }: { questionGroup: Group; isMobile: boolean }) {
  const { productName, name, productId } = questionGroup;
  const [sidebarQuestions, otherQuestions] = _.partition(questionGroup.subgroups, subgroup => {
    return subgroup.type === "sidebar";
  });

  const parseProductHeader = () => {
    if (productId && productName) {
      return `Product: ${productName}`;
    }
    if (productId && !productName && name !== 'Product') {
      return `Product: ${name}`;
    }
    return name || "Product";
  }

  return (
    <>
          {!isMobile && <div className={styles.sectionSeparator} />}

      <div className={cx(styles.questionGroup, isMobile && styles.mobileQuestionGroup)}>
        <ImageGroup>
          <div className={cx(styles.sidebar, isMobile && styles.mobileSidebar)}>
            <Text.Display5 kind="primary" className={styles.sidebarTitle}>
              {parseProductHeader()}
            </Text.Display5>

            {sidebarQuestions.map((sidebarSubgroup, index) => {
              return <SubgroupView key={sidebarSubgroup.id || `sidebar-${index}`} subgroup={sidebarSubgroup} isMobile={isMobile} />;
            })}
          </div>

          <div className={styles.content}>
            {otherQuestions.map((subgroup, index) => {
              return <SubgroupView key={subgroup.id || `other-${index}`} subgroup={subgroup} isMobile={isMobile} productId={productId} />;
            })}
          </div>
        </ImageGroup>
      </div>
    </>
  );
}

function AnswerView(
  question: Question & {
    selectable?: boolean;
    updateSelected?: (image: string) => any;
    productId?: string | null;
    isLastQuestion: boolean;
  },
) {
  const { answerType, answerState, answerJson, id, shortTitle, selectable, updateSelected, productId } = question;
  const parsedAnswer = answerJson ? JSON.parse(answerJson) : undefined;

  const gig = React.useContext(GigContext);

  switch (answerType) {
    case "file": {
      if (!parsedAnswer || parsedAnswer.length === 0) {
        return (
          <div className={styles.fullWidth}>
            <Text.P2 kind="error">Not answered</Text.P2>
          </div>
        );
      }
      return (
        <>
          {parsedAnswer.map((url: string, i: number) => (
            <a
              key={`${url}${id}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 12 }}
            >
              <i className="fa-solid fa-file" />
              {`View file ${i + 1}`}
            </a>
          ))}
        </>
      );
    }
    case "image": {
      const profilePictures =
        gig?.products
          .filter(p => p.id === productId)
          .flatMap(({ profilePictures }) =>
            (profilePictures ?? [])
              .filter((p): p is { url: string; id: string; __typename: "LinkDocument" } => !!p)
              .map(p => ({ url: p.url, shortTitle: "Reference Img" })),
          ) || [];

      if (!parsedAnswer && profilePictures?.length === 0) {
        return (
          <React.Fragment key={id}>
            <div className={styles.fullWidth}>
              <Text.P2 kind="error">Not answered</Text.P2>
            </div>
          </React.Fragment>
        );
      }

      if (parsedAnswer) {
        let fileName: string | undefined = undefined;

        if (gig && gig.organizationLocation && gig.assignedGogetter) {
          const date = gig.startTime && gig.timezone ? moment.tz(gig.startTime, gig.timezone).format("YYYMMDD") : "";
          fileName = `${date}-${gig.customLocationName}`;

          if (gig.organizationLocation) {
            fileName = fileName + `-${gig.organizationLocation?.internalIdentifier}`;
          }

          fileName = fileName + `-${gig.assignedGogetter.firstName} ${gig.assignedGogetter.lastName}`;
        }

        const parsedWithProfilePictures = [
          ...parsedAnswer.map((url: string) => ({ url, shortTitle })),
          ...profilePictures,
        ];

        return (
          <>
            {parsedWithProfilePictures.map(({ url, shortTitle }) => {
              return (
                <Thumbnail
                  key={`${url}${id}`}
                  image={url}
                  size={160}
                  title={shortTitle}
                  selectable={selectable}
                  updateSelected={updateSelected}
                  fileName={fileName}
                  className={styles.thumbnailItem}
                />
              );
            })}
          </>
        );
      } else {
        return (
          <>
            {profilePictures.map(({ url, shortTitle }) => {
              return (
                <Thumbnail
                  key={`${url}${id}`}
                  image={url}
                  size={160}
                  title={shortTitle}
                  selectable={selectable}
                  updateSelected={updateSelected}
                  fileName={""}
                  className={styles.thumbnailItem}
                />
              );
            })}
          </>
        );
      }
    }

    case "text":
    case "number":
    case "decimal":
    case "single_select":
    case "multiple_choice_one":
      return (
        <React.Fragment key={id}>
          <Text.P2>
            <AnswerContent question={question}>{parsedAnswer}</AnswerContent>
          </Text.P2>
        </React.Fragment>
      );
    case "atom_choice_one":
      if (!parsedAnswer) {
        return (
          <React.Fragment key={id}>
            <Text.P3 kind="error">Not answered</Text.P3>
          </React.Fragment>
        );
      }
      const atom = parsedAnswer?.atoms?.[0];
      const other = parsedAnswer?.other;
      return (
        <React.Fragment key={id}>
          <Text.P3>
            <AnswerContent question={question}>{atom ? atomToLabel(atom) : other}</AnswerContent>
          </Text.P3>
        </React.Fragment>
      );
    case "atom_choice_many":
      if (!parsedAnswer) {
        return (
          <React.Fragment key={id}>
            <Text.P3 kind="error">Not answered</Text.P3>
          </React.Fragment>
        );
      }
      const hasAtoms = parsedAnswer?.atoms?.length ?? 0 > 0;
      return (
        <React.Fragment key={id}>
          <Text.P3>
            <AnswerContent question={question}>
              {hasAtoms ? parsedAnswer.atoms.map((atom: Atom) => atomToLabel(atom)).join(", ") : null}
              <br />
              {parsedAnswer?.other ?? null}
            </AnswerContent>
          </Text.P3>
        </React.Fragment>
      );
    case "toggle":
      return (
        <React.Fragment key={id}>
          <Text.P3>
            <AnswerContent question={question}>{parsedAnswer === true ? "Yes" : "No"}</AnswerContent>
          </Text.P3>
        </React.Fragment>
      );
    case "date":
      let formattedDate;
      try {
        formattedDate = parsedAnswer && format(parseISO(parsedAnswer), "MM/dd/yyyy");
      } catch (err) {
        console.warn("Couldn't format date", parsedAnswer);
      }
      return (
        <React.Fragment key={id}>
          <Text.P3>
            <AnswerContent question={question}>{formattedDate}</AnswerContent>
          </Text.P3>
        </React.Fragment>
      );

    case "multiple_choice_many":
    case "multi_select":
      return (
        <Text.P3 key={id}>
          <AnswerContent question={question}>
            {Array.isArray(parsedAnswer) ? parsedAnswer.join(", ") : parsedAnswer}
          </AnswerContent>
        </Text.P3>
      );

    case "expense":
      return (
        <div key={id}>
          <div className={styles.expenseAmount}>
            <Text.H3 kind="secondary">Amount:&nbsp;</Text.H3>
            <Text.P3>
              <AnswerContent question={question}>{parsedAnswer?.amount}</AnswerContent>
            </Text.P3>
          </div>
          {parsedAnswer &&
            parsedAnswer.images &&
            parsedAnswer.images.map(({ url, caption }: { url: string; caption: string }) => {
              return (
                <div key={`${url}${id}`} className={styles.expenseItem}>
                  <Thumbnail image={url} size={160} className={styles.thumbnailItem} />
                  <Text.P4>{caption}</Text.P4>
                </div>
              );
            })}
        </div>
      );

    case "money":
    case "money_with_negative":
      return (
        <Text.P3 key={id}>
          <AnswerContent question={question}>{parsedAnswer && <Money value={parsedAnswer} />}</AnswerContent>
        </Text.P3>
      );

    case "location":
      return (
        <Text.P3 key={id}>
          <AnswerContent question={question}>{parsedAnswer && parsedAnswer.description}</AnswerContent>
        </Text.P3>
      );
    case "temperature":
      return (
        <Text.P3 key={id}>
          <AnswerContent question={question}>
            {parsedAnswer.kelvin &&
              (parsedAnswer.unit === "celsius"
                ? toCelsius(parsedAnswer.kelvin, TemperatureUnit.Kelvin).toFixed(1) + " °C"
                : toFahrenheit(parsedAnswer.kelvin, TemperatureUnit.Kelvin).toFixed(1) + " °F")}
          </AnswerContent>
        </Text.P3>
      );
    case "agreement_signature":
      return (
        <Text.P3 key={id}>
          <AnswerContent question={question}>
            Signature: {parsedAnswer && parsedAnswer.signature}, agreed:{" "}
            {parsedAnswer && parsedAnswer.agreed ? "Yes" : "No"}
          </AnswerContent>
        </Text.P3>
      );
    case "contact":
      if (!parsedAnswer) {
        return (
          <Text.P3 key={id} kind="error">
            Not answered
          </Text.P3>
        );
      }
      const contactKeys = Object.keys(parsedAnswer);
      return (
        <div key={id} className={styles.contactAnswer}>
          {contactKeys.map(key => {
            const value = parsedAnswer[key];
            const isEmpty = !value || value.trim() === '';
            return (
              <Text.P3 key={key}>
                <span style={{display: "flex", marginBottom: '2px'}}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}:
                  {isEmpty ? <Text.P3 kind="error">&nbsp;Not answered</Text.P3> : value}
                </span>
              </Text.P3>
            );
          })}
        </div>
      );

    default:
      return (
        <Text.P3 key={id}>
          <AnswerContent question={question}>{(parsedAnswer || parsedAnswer === 0) && parsedAnswer}</AnswerContent>
        </Text.P3>
      );
  }
}

function getStatItemValue(question: Question) {
  const { answerState, answerJson, answerType } = question;

  if (isUnanswered(answerState)) {
    return "Not answered";
  }

  const parsedAnswer = JSON.parse(answerJson!);

  if (answerState === "answered" && parsedAnswer) {
    if (answerType === "temperature") {
      return (
        parsedAnswer.kelvin &&
        (parsedAnswer.unit === "celsius"
          ? toCelsius(parsedAnswer.kelvin, TemperatureUnit.Kelvin).toFixed(1) + " °C"
          : toFahrenheit(parsedAnswer.kelvin, TemperatureUnit.Kelvin).toFixed(1) + " °F")
      );
    }
    return parsedAnswer;
  }

  if (answerState === "na") {
    return "N/A";
  }

  return "Not answered";
}

type Questions = Subgroup["questions"];

function SubgroupView({
  subgroup,
  isMobile,
  productId,
}: {
  subgroup: Subgroup;
  isMobile: boolean;
  productId?: string | null;
}) {
  const { name, questions: qs } = subgroup;
  // Filter out questions with "unasked" state - these are conditional questions that weren't shown
  const questions = useMemo(() => (qs || []).filter(q => q.answerState !== "unasked"), [qs]);

  if (questions.length === 0) {
    return null;
  }

  switch (subgroup.type) {
    case "sibling":
      return (
        <div className={styles.siblingGrid}>
          {questions.map((question, index) => {
            let questionTitle = question.columnTitle || question.gogetterTitle;
            if (question.answerType === "money" || question.answerType === "money_with_negative") {
              questionTitle = "Pricing";
            } else if (question.answerType === "toggle") {
              questionTitle = "On Promo";
            }

            return (
              <QuestionWrapper question={question} key={question.id}>
                <div className={styles.siblingItem}>
                  {questionTitle && (
                    <div className={styles.questionLabel}>
                      {questionTitle}
                    </div>
                  )}
                  <div className={styles.siblingAnswer}>
                    <AnswerView {...question} productId={productId} isLastQuestion={index === questions.length - 1} />
                  </div>
                </div>
              </QuestionWrapper>
            );
          })}
        </div>
      );

    case "sidebar":
      return (
        <React.Fragment>
          <Text.Display5 kind="secondary" className={styles.sidebarQuestion}>
            {name}
          </Text.Display5>
          {questions.map(question => {
            const answer = JSON.parse(question.answerJson!);

            if (question.answerType === "contact") {
              const answerKeys = answer === null || answer === undefined ? [] : Object.keys(answer);

              return (
                <QuestionWrapper question={question} key={question.id}>
                  {answerKeys.length === 0 ? (
                    <Text.P3 kind="error" className={styles.sidebarAnswer}>Not answered</Text.P3>
                  ) : (
                    answerKeys.map(key => {
                      const value = answer[key];
                      const isEmpty = !value || value.trim() === '';
                      return (
                        <React.Fragment key={key}>
                          <Text.H3 kind="secondary">{key.charAt(0).toUpperCase() + key.slice(1)}: </Text.H3>
                          <Text.P3 className={styles.sidebarAnswer} kind={isEmpty ? "error" : undefined}>
                            {isEmpty ? 'Not answered' : value}
                          </Text.P3>
                        </React.Fragment>
                      );
                    })
                  )}
                </QuestionWrapper>
              );
            }

            if (question.answerType === "atom_choice_one") {
              if (answer === null || answer === undefined) {
                return (
                  <QuestionWrapper question={question} key={question.id}>
                    <Text.P3 kind="error" className={styles.sidebarAnswer}>Not answered</Text.P3>
                  </QuestionWrapper>
                );
              }
              return (
                <QuestionWrapper question={question} key={question.id}>
                  <Text.P3 className={styles.sidebarAnswer}>
                    <AnswerContent question={question}>
                      {answer.atoms?.[0] ? atomToLabel(answer.atoms[0]) : answer.other}
                    </AnswerContent>
                  </Text.P3>
                </QuestionWrapper>
              );
            }
            if (question.answerType === "atom_choice_many") {
              if (answer === null || answer === undefined) {
                return (
                  <QuestionWrapper question={question} key={question.id}>
                    <Text.P3 kind="error" className={styles.sidebarAnswer}>Not answered</Text.P3>
                  </QuestionWrapper>
                );
              }
              const hasAtoms = answer.atoms && answer.atoms.length > 0;
              return (
                <QuestionWrapper question={question} key={question.id}>
                  <Text.P3 className={styles.sidebarAnswer}>
                    <AnswerContent question={question}>
                      {hasAtoms ? answer.atoms.map((atom: Atom) => atomToLabel(atom)).join(", ") : null}
                      <br />
                      {answer.other ? answer.other : null}
                    </AnswerContent>
                  </Text.P3>
                </QuestionWrapper>
              );
            }
            return (
              <QuestionWrapper question={question}>
                <Text.P3 key={question.id} className={styles.sidebarAnswer}>
                  <AnswerContent question={question}>{answer}</AnswerContent>
                </Text.P3>
              </QuestionWrapper>
            );
          })}
        </React.Fragment>
      );

    case "simple":
      return (
        <React.Fragment>
          <div className={styles.simpleAnswers}>
            {questions.map((question, index) => {
              const questionTitle = question.columnTitle || question.gogetterTitle;

              return (
                <QuestionWrapper key={question.id} question={question}>
                  <div className={styles.questionBlock}>
                    {questionTitle && (
                      <div className={styles.questionLabel}>
                        {questionTitle}
                      </div>
                    )}
                    <div className={styles.answerBlock}>
                      <AnswerView {...question} productId={productId} isLastQuestion={index === questions.length - 1} />
                    </div>
                  </div>
                </QuestionWrapper>
              );
            })}
          </div>
        </React.Fragment>
      );

    case "stats":
      return (
        <>
          <div className={styles.statsBoard}>
            <StatsBoard loading={false} size={"medium"}>
              {questions.map(question => {
                return (
                  <StatsItem
                    bgColor={variables.gray5}
                    key={question.id}
                    name={question.columnTitle || question.gogetterTitle || ""}
                    textColor={variables.gray1}
                    value={getStatItemValue(question)}
                    format={question.answerType === "temperature" ? "temperature" : undefined}
                  />
                );
              })}
            </StatsBoard>
          </div>
          <div>
            {questions.map(question => {
              const value = getStatItemValue(question);
              const questionTitle = question.columnTitle || question.gogetterTitle || "";
              const isNotAnswered = value === "Not answered";
              return (
                <div key={question.id} className={styles.statItem}>
                  {questionTitle && (
                    <div className={styles.questionLabel}>
                      {questionTitle}
                    </div>
                  )}
                  <Text.P3 className={styles.statValue} kind={isNotAnswered ? "error" : undefined}>{value}</Text.P3>
                </div>
              );
            })}
          </div>
        </>
      );

    case null:
    case undefined:
    default:
      // Fallback to simple rendering for null/undefined types
      return (
        <React.Fragment>
          <div className={styles.simpleAnswers}>
            {questions.map((question, index) => {
              const questionTitle = question.columnTitle || question.gogetterTitle;

              return (
                <QuestionWrapper key={question.id} question={question}>
                  <div className={styles.questionBlock}>
                    {questionTitle && (
                      <div className={styles.questionLabel}>
                        {questionTitle}
                      </div>
                    )}
                    <div className={styles.answerBlock}>
                      <AnswerView {...question} productId={productId} isLastQuestion={index === questions.length - 1} />
                    </div>
                  </div>
                </QuestionWrapper>
              );
            })}
          </div>
        </React.Fragment>
      );
  }
}

function QuestionWrapper({ children, question }: { children: React.ReactNode; question: Question }) {
  return (
    <div
      className={question.answerType === "image" ? styles.imagesWrapper : ""}
    >
      {children}
    </div>
  );
}

function AnswerContent({ question, children }: { question: Question; children: any }) {
  // Show "Not answered" for unanswered questions (null or "unasked")
  if (isUnanswered(question.answerState)) {
    return <Text.P3 kind="error">Not answered</Text.P3>;
  }

  if (question.answerState === "answered" && children) {
    return children;
  }

  if (question.answerState === "na") {
    return question.notApplicableDesc || "Not Applicable";
  }

  if (question.answerState === "answered" && question.answerType === "number" && question.answerJson === "0") {
    return question.answerJson;
  }

  return <Text.P3 kind="error">Not answered</Text.P3>;
}

const GigContext = React.createContext<GigReportOverviewFragment | null>(null);

function renderStatsAndReports(gig: GigReportOverviewFragment, gigReportSteps: GigReportStepsQuery["gigReportSteps"] | null) {
  // Use gigReportSteps if available, otherwise fall back to gig.report
  const processedReport = React.useMemo(() => {
    if (gigReportSteps && gigReportSteps.length > 0) {
      const parsed = parseGigReportSteps(gigReportSteps, gig.products);
      return parsed;
    }

    const nullGroups = gig.report.filter(g => g.id === null);
    const validGroups = gig.report.filter(g => g.id !== null);



    if (nullGroups.length === 0) {
      return validGroups;
    }

    // Find "General & Setup" group (non-product specific one)
    const generalSetupIndex = validGroups.findIndex(
      g => g.name === "General & Setup" && g.productId === null
    );

    if (generalSetupIndex === -1) {
      // If no "General & Setup" found, just return all groups
      return gig.report;
    }

    // Merge null group questions into "General & Setup"
    const generalSetup = validGroups[generalSetupIndex];
    const mergedSubgroups = [
      ...generalSetup.subgroups,
      ...nullGroups.flatMap(g => g.subgroups),
    ];

    const mergedGroup = {
      ...generalSetup,
      subgroups: mergedSubgroups,
    };

    // Replace the "General & Setup" group with merged version
    return [
      ...validGroups.slice(0, generalSetupIndex),
      mergedGroup,
      ...validGroups.slice(generalSetupIndex + 1),
    ];
  }, [gigReportSteps, gig.report, gig.products]);

  return (
    <GigContext.Provider value={gig}>
      <Mobile>
        {isMobile => (
          <div className={styles.generalStats}>
            {processedReport.length > 0 && (
              <div className={styles.reportContainer}>
                {processedReport.map((questionGroup, index) => {
                  return (
                    <GroupView
                      key={`${questionGroup.id || index}-${questionGroup.productId || 'no-product'}`}
                      questionGroup={questionGroup}
                      isMobile={isMobile}
                    />
                  );
                })}
              </div>
            )}

            {(() => {
              const profilePictures = gig?.products.flatMap(({ profilePictures }) =>
                (profilePictures || []).map(p => p!.url),
              );
              let allImages: string[] = [];

              for (let group of processedReport) {
                for (let subgroup of group.subgroups) {
                  for (let question of subgroup.questions) {
                    if (question.answerType === "image" && question.answerJson) {
                      const images = JSON.parse(question.answerJson);
                      if (images) {
                        for (let image of images) {
                          allImages.push(image);
                        }
                      }
                    }
                  }
                }
              }
              allImages = [...allImages, ...profilePictures];

              if (allImages.length === 0) {
                return null;
              }

              return (
                <div className={styles.multipleDownload}>
                  <Button
                    kind={"primaryGradient"}
                    onClick={() => {
                      downloadUrl(zipFile(allImages), "report-images.zip");
                    }}
                  >
                    Download all images
                  </Button>
                </div>
              );
            })()}
          </div>
        )}
      </Mobile>
    </GigContext.Provider>
  );
}

export default function FieldReportView({ gig, gigReportSteps, loadingGigDetails }: Props) {
  return gig && !loadingGigDetails ? renderStatsAndReports(gig, gigReportSteps) : <LoadingContainer center message={""} />;
}
